import type { QuickFillItem } from '../../../shared/types';
import { perf } from '../../../shared/lib/perfLogger';
import { normalizeRevitParameterName } from '../../../shared/lib/revitParameterNames';
import {
  EMPTY_RESOLVED,
  normalize,
  rowKey,
  stableTypeKey,
  type AvailableParameterMeta,
  type AvailableParameterScopeEntry,
  type CheckedProperty,
  type CheckedPropertyWithLookup,
  type ParameterBindingScope,
  type PropertyPopulationSummary,
  type ResolvedParam,
} from './quickFillDataTypes';

const buildAvailableParameterScopeIndex = (
  availableParameters: AvailableParameterMeta[]
): Map<string, AvailableParameterScopeEntry> => {
  const scopeMap = new Map<string, AvailableParameterScopeEntry>();

  availableParameters.forEach((parameter) => {
    const normalizedName = normalize(normalizeRevitParameterName(parameter?.name || ''));
    if (!normalizedName) {
      return;
    }

    const entry = scopeMap.get(normalizedName) ?? { instance: false, type: false };
    if (parameter?.isInstance === true) {
      entry.instance = true;
    } else {
      entry.type = true;
    }
    scopeMap.set(normalizedName, entry);
  });

  return scopeMap;
};

const resolveParamValue = (
  familyName: string,
  typeName: string | null,
  typeParamMap: Map<string, ResolvedParam>,
  parameterName: string,
  instanceParamIndex: Map<string, Map<string, ResolvedParam>>,
  availableParameterNames: Set<string>,
  scopeHint: ParameterBindingScope | undefined,
  instanceTargetIndex: Map<string, Map<string, string[]>> = new Map(),
  familyTypeId?: string | null
): ResolvedParam => {
  const typeIndexKey = stableTypeKey(familyTypeId);
  const rowIndexKey = rowKey(familyName, typeName);
  const normalizedParameterName = normalize(normalizeRevitParameterName(parameterName));
  const withInstanceTargets = (resolved: ResolvedParam): ResolvedParam => {
    const targetElementIds =
      (typeIndexKey ? instanceTargetIndex.get(typeIndexKey)?.get(parameterName) : undefined) ??
      instanceTargetIndex.get(rowIndexKey)?.get(parameterName) ??
      [];
    if (resolved.bindingScope !== 'instance' || targetElementIds.length === 0) {
      return resolved;
    }

    return {
      ...resolved,
      instanceTargetElementIds: targetElementIds,
    };
  };

  const fromStableType = typeIndexKey ? instanceParamIndex.get(typeIndexKey)?.get(parameterName) : undefined;
  const fromExact = instanceParamIndex.get(rowIndexKey)?.get(parameterName);
  const fromFamily = instanceParamIndex.get(rowKey(familyName, null))?.get(parameterName);
  const fromType = typeParamMap.get(parameterName);
  const fromInstance = fromStableType ?? fromExact;

  if (scopeHint === 'type') {
    // The configured destination describes how new parameters should be
    // created. An already-existing editable-family parameter can have a
    // different real scope, and its family-document snapshot is still the
    // authoritative value for this family/type row.
    if (fromType) {
      return fromType;
    }
    if (availableParameterNames.has(normalizedParameterName)) {
      return {
        value: '',
        isReadOnly: false,
        hasParameter: false,
        bindingScope: 'type',
      };
    }
    return EMPTY_RESOLVED;
  }
  if (scopeHint === 'instance') {
    if (fromInstance?.bindingScope === 'instance') return withInstanceTargets(fromInstance);
    if (fromType?.bindingScope === 'instance') return fromType;
    if (fromFamily?.bindingScope === 'instance') {
      return { ...fromFamily, hasParameter: false, isReadOnly: false, bindingScope: 'instance' };
    }
    if (availableParameterNames.has(normalizedParameterName)) {
      return {
        value: '',
        isReadOnly: false,
        hasParameter: false,
        bindingScope: 'instance',
      };
    }
    return EMPTY_RESOLVED;
  }
  if (fromInstance) return withInstanceTargets(fromInstance);
  if (fromFamily) return { ...fromFamily, hasParameter: false, isReadOnly: false, bindingScope: 'instance' };
  if (fromType) return fromType;
  if (availableParameterNames.has(normalizedParameterName)) {
    return {
      value: '',
      isReadOnly: false,
      hasParameter: false,
      bindingScope: scopeHint ?? 'unknown',
    };
  }
  return EMPTY_RESOLVED;
};

const resolveInstanceScopedParamValue = (
  exactInstanceParamMap: Map<string, ResolvedParam>,
  typeParamMap: Map<string, ResolvedParam>,
  parameterName: string,
  availableParameterNames: Set<string>,
  scopeHint: ParameterBindingScope | undefined
): ResolvedParam => {
  const exactInstance = exactInstanceParamMap.get(parameterName);
  if (exactInstance?.bindingScope === 'instance') {
    return exactInstance;
  }

  if (scopeHint === 'instance') {
    const normalizedParameterName = normalize(normalizeRevitParameterName(parameterName));
    if (availableParameterNames.has(normalizedParameterName)) {
      return {
        value: '',
        isReadOnly: false,
        hasParameter: false,
        bindingScope: 'instance',
      };
    }

    return EMPTY_RESOLVED;
  }

  const typeValue = typeParamMap.get(parameterName);
  if (typeValue) {
    return typeValue;
  }

  const normalizedParameterName = normalize(normalizeRevitParameterName(parameterName));
  if (availableParameterNames.has(normalizedParameterName)) {
    return {
      value: '',
      isReadOnly: false,
      hasParameter: false,
      bindingScope: scopeHint ?? 'unknown',
    };
  }

  return EMPTY_RESOLVED;
};

const setItemValue = (
  item: QuickFillItem, label: string, resolved: ResolvedParam, overrideNonEmpty: boolean
): boolean => {
  const current = item[label as keyof QuickFillItem];
  let changed = false;

  if (overrideNonEmpty || !(typeof current === 'string' && current.trim() !== '')) {
    const currentValue = current === null || current === undefined ? '' : String(current);
    if (currentValue !== resolved.value || !Object.prototype.hasOwnProperty.call(item, label)) {
      item[label as keyof QuickFillItem] = resolved.value;
      changed = true;
    }
  }

  return applyResolvedMetadataToItem(item, label, resolved) || changed;
};

const applyResolvedMetadataToItem = (
  item: QuickFillItem,
  label: string,
  resolved: ResolvedParam
): boolean => {
  let changed = false;
  const flags = (((item as any).__readonlyFlags as Record<string, boolean>) || {});
  if (flags[label] !== resolved.isReadOnly) {
    flags[label] = resolved.isReadOnly;
    (item as any).__readonlyFlags = flags;
    changed = true;
  }
  const hasParamFlags = (((item as any).__hasParamFlags as Record<string, boolean>) || {});
  if (hasParamFlags[label] !== resolved.hasParameter) {
    hasParamFlags[label] = resolved.hasParameter;
    (item as any).__hasParamFlags = hasParamFlags;
    changed = true;
  }
  const bindingScopeFlags = (((item as any).__bindingScopeFlags as Record<string, string>) || {});
  if (bindingScopeFlags[label] !== resolved.bindingScope) {
    bindingScopeFlags[label] = resolved.bindingScope;
    (item as any).__bindingScopeFlags = bindingScopeFlags;
    changed = true;
  }

  const instanceTargets = (((item as any).__instanceTargetElementIdsByField as Record<string, string[]> | undefined) || {});
  const nextTargets =
    resolved.bindingScope === 'instance' && resolved.hasParameter
      ? Array.from(new Set((resolved.instanceTargetElementIds || []).map((id) => id.trim()).filter(Boolean)))
      : [];
  const previousTargets = instanceTargets[label] || [];
  const targetsChanged =
    previousTargets.length !== nextTargets.length ||
    previousTargets.some((id, index) => id !== nextTargets[index]);
  if (targetsChanged) {
    if (nextTargets.length > 0) {
      instanceTargets[label] = nextTargets;
      (item as any).__instanceTargetElementIdsByField = instanceTargets;
    } else {
      delete instanceTargets[label];
      if (Object.keys(instanceTargets).length > 0) {
        (item as any).__instanceTargetElementIdsByField = instanceTargets;
      } else {
        delete (item as any).__instanceTargetElementIdsByField;
      }
    }
    changed = true;
  }

  return changed;
};

const buildPopulationSummary = (
  rows: QuickFillItem[],
  checkedProps: CheckedProperty[]
): PropertyPopulationSummary[] => checkedProps.map((prop) => {
  let populatedRows = 0;
  let rowsWithParameter = 0;
  let readOnlyRows = 0;
  const sampleValues: string[] = [];

  rows.forEach((row) => {
    const value = row[prop.label as keyof QuickFillItem];
    const readonlyFlags = (((row as any).__readonlyFlags as Record<string, boolean>) || {});
    const hasParamFlags = (((row as any).__hasParamFlags as Record<string, boolean>) || {});
    const normalizedValue = typeof value === 'string' ? value.trim() : value;

    if (hasParamFlags[prop.label] !== false) {
      rowsWithParameter += 1;
    }
    if (readonlyFlags[prop.label] === true) {
      readOnlyRows += 1;
    }
    if (typeof normalizedValue === 'string' ? normalizedValue !== '' : normalizedValue !== undefined && normalizedValue !== null) {
      populatedRows += 1;
      if (sampleValues.length < 3) {
        sampleValues.push(String(value));
      }
    }
  });

  return {
    label: prop.label,
    revitParameter: prop.revitParameter,
    populatedRows,
    rowsWithParameter,
    readOnlyRows,
    sampleValues,
  };
});

const buildSampleRows = (rows: QuickFillItem[], checkedProps: CheckedProperty[]) =>
  rows.slice(0, 3).map((row) => ({
    Name: row.Name,
    values: checkedProps.reduce<Record<string, string>>((acc, prop) => {
      const rawValue = row[prop.label as keyof QuickFillItem];
      acc[prop.label] = typeof rawValue === 'string' ? rawValue : rawValue == null ? '' : String(rawValue);
      return acc;
    }, {}),
  }));

const emitPopulationDiagnostics = (
  stage: string,
  rows: QuickFillItem[],
  checkedProps: CheckedProperty[],
  meta: Record<string, unknown> = {}
) => {
  const population = buildPopulationSummary(rows, checkedProps);
  const logPayload = {
    ...meta,
    rows: rows.length,
    population,
    sampleRows: buildSampleRows(rows, checkedProps),
  };

  perf.log('QF-Builder', `${stage} population summary`, logPayload);

  const totalPopulated = population.reduce((sum, item) => sum + item.populatedRows, 0);
  const totalResolvedParameters = population.reduce((sum, item) => sum + item.rowsWithParameter, 0);

  if (rows.length === 0 || population.length === 0 || totalPopulated > 0) {
    return;
  }

  if (totalResolvedParameters > 0) {
    perf.log('QF-Builder', `${stage} found checked Quick Fill parameters with empty project values`, logPayload);
    return;
  }

  perf.warn('QF-Builder', `${stage} could not find any checked Quick Fill parameters in the project payload`, logPayload);
};

const applyResolvedValueToItem = (
  item: QuickFillItem,
  label: string,
  resolved: ResolvedParam,
  overrideNonEmpty: boolean
): boolean => {
  return setItemValue(item, label, resolved, overrideNonEmpty);
};

const applySourceResolvedValueToItem = (
  item: QuickFillItem,
  label: string,
  resolved: ResolvedParam,
  preserveValue: boolean
): boolean => {
  if (preserveValue) {
    return applyResolvedMetadataToItem(item, label, resolved);
  }

  return applyResolvedValueToItem(item, label, resolved, true);
};

const isEditedQuickFillCell = (
  editedCells: Record<string, boolean>,
  rowIndex: number,
  label: string
): boolean => rowIndex >= 0 && editedCells?.[`${rowIndex}-${label}`] === true;

export {
  buildAvailableParameterScopeIndex,
  resolveParamValue,
  resolveInstanceScopedParamValue,
  setItemValue,
  applyResolvedMetadataToItem,
  applyResolvedValueToItem,
  applySourceResolvedValueToItem,
  isEditedQuickFillCell,
  buildPopulationSummary,
  buildSampleRows,
  emitPopulationDiagnostics,
};

export type {
  CheckedProperty,
  CheckedPropertyWithLookup,
  AvailableParameterMeta,
  ParameterBindingScope,
  AvailableParameterScopeEntry,
  ResolvedParam,
  PropertyPopulationSummary,
};
